// ============================================================
// ORDER CANCELLATIONS PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Table, Badge, Button, Modal, StatCard } from '../components/ui';
import { getCancellations } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatDateTime, formatCurrency } from '../utils/helpers';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#3B82F6', '#EF4444', '#F59E0B', '#10B981', '#8B5CF6'];

const OrderCancellations = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(null);

  useEffect(() => {
    adminApi.getCancellations(getCancellations)
      .then(res => {
        const items = Array.isArray(res) ? res : (res?.items || []);
        // Normalize field names from backend Order model
        setData(items.map(o => ({
          ...o,
          orderId:        o.orderId || o.order_id || o.id,
          customerName:   o.customerName || o.customer_name || o.customer || '—',
          restaurantName: o.restaurantName || o.restaurant_name || o.restaurant || '—',
          amount:         o.amount || o.total_amount || 0,
          cancelledBy:    o.cancelledBy || o.cancelled_by || 'customer',
          reason:         o.reason || o.cancellation_reason || '—',
          refundStatus:   o.refundStatus || o.refund_status || 'pending',
          refundAmount:   o.refundAmount || o.refund_amount || 0,
          cancelledAt:    o.cancelledAt || o.cancelled_at || o.createdAt,
        })));
        setLoading(false);
      })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, []);

  const byInitiator = data.reduce((acc, r) => { acc[r.cancelledBy] = (acc[r.cancelledBy] || 0) + 1; return acc; }, {});
  const pieData = Object.entries(byInitiator).map(([name, value]) => ({ name, value }));

  const refundable = data.filter(r => r.refundStatus === 'completed').length;
  const totalRefund = data.reduce((s, r) => s + (r.refundAmount || 0), 0);

  const columns = [
    { key: 'orderId', label: 'Order ID', render: v => <code>#{v}</code> },
    { key: 'customerName', label: 'Customer', render: v => <b>{v}</b> },
    { key: 'restaurantName', label: 'Restaurant' },
    { key: 'amount', label: 'Order Amt', render: v => formatCurrency(v) },
    { key: 'cancelledBy', label: 'Cancelled By', render: v => <Badge status={v === 'customer' ? 'info' : v === 'restaurant' ? 'warning' : 'secondary'} text={v} /> },
    { key: 'reason', label: 'Reason' },
    { key: 'refundStatus', label: 'Refund', render: v => <Badge status={v === 'completed' ? 'active' : v === 'pending' ? 'pending' : 'inactive'} text={v} /> },
    { key: 'cancelledAt', label: 'Cancelled At', render: v => formatDateTime(v) },
    { key: 'id', label: '', render: (_, row) => <Button size="sm" variant="secondary" onClick={() => setView(row)}>Details</Button> },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Order Cancellations</h1>
          <p className="admin-page-subtitle">Monitor cancellation patterns and refund status</p>
        </div>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Cancellations" value={data.length} icon={<AlertTriangle size={18} />} color="#EF4444" />
        <StatCard label="Refunds Completed" value={refundable} icon={<AlertTriangle size={18} />} color="#10B981" />
        <StatCard label="Pending Refunds" value={data.filter(r => r.refundStatus === 'pending').length} icon={<AlertTriangle size={18} />} color="#F59E0B" />
        <StatCard label="Total Refunded" value={formatCurrency(totalRefund)} icon={<AlertTriangle size={18} />} color="#3B82F6" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 24, marginBottom: 24 }}>
        <div className="admin-card">
          <div className="admin-card-header"><h3 className="admin-card-title">Cancellation By</h3></div>
          <div className="admin-card-body">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="admin-card">
          <div className="admin-card-header"><h3 className="admin-card-title">Top Cancellation Reasons</h3></div>
          <div className="admin-card-body">
            {Object.entries(data.reduce((acc, r) => { acc[r.reason] = (acc[r.reason] || 0) + 1; return acc; }, {}))
              .sort((a, b) => b[1] - a[1]).slice(0, 6).map(([reason, count], i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--admin-border)' }}>
                  <span style={{ fontSize: 13 }}>{reason}</span>
                  <strong style={{ color: '#EF4444' }}>{count}</strong>
                </div>
              ))}
          </div>
        </div>
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <span style={{ fontSize: 14, fontWeight: 700 }}>{data.length} Cancellations</span>
        </div>
        <Table columns={columns} data={data} loading={loading} />
      </div>

      <Modal open={!!view} onClose={() => setView(null)} title="Cancellation Details"
        footer={<Button variant="secondary" onClick={() => setView(null)}>Close</Button>}>
        {view && (
          <div className="admin-info-grid">
            {[
              ['Order ID', `#${view.orderId}`], ['Customer', view.customerName],
              ['Restaurant', view.restaurantName], ['Order Amount', formatCurrency(view.amount)],
              ['Cancelled By', view.cancelledBy], ['Reason', view.reason],
              ['Refund Status', view.refundStatus], ['Refund Amount', formatCurrency(view.refundAmount || 0)],
              ['Cancelled At', formatDateTime(view.cancelledAt)],
            ].map(([l, v]) => (
              <div key={l} className="admin-info-item">
                <span className="admin-info-label">{l}</span>
                <span className="admin-info-value">{v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrderCancellations;
